import { clearSession, session, setPlayer } from "./session";
import type { HallMessage, Player } from "./protocol";
import { socketUrl } from "./endpoints";

type Listener = (message: HallMessage) => void;
export class HallResponseError extends Error {}
class HallConnection {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private pending = new Map<
    string,
    {
      resolve: (data: unknown) => void;
      reject: (error: Error) => void;
    }
  >();
  private heartbeat?: ReturnType<typeof setInterval>;
  private retry?: ReturnType<typeof setTimeout>;
  private connecting: Promise<void> | null = null;
  private abortConnection?: () => void;
  private stopped = true;
  machineId = 0;

  connect(): Promise<void> {
    if (session.ready && this.socket?.readyState === WebSocket.OPEN)
      return Promise.resolve();
    if (this.connecting) return this.connecting;
    if (!session.token) return Promise.reject(new Error("请先登录"));
    const token = session.token;
    this.stopped = false;
    this.connecting = new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(
        socketUrl('/game/websocket'),
      );
      this.socket = socket;
      this.abortConnection = () => {
        reject(new Error("大厅连接已断开"));
      };
      socket.onopen = () =>
        socket.send(JSON.stringify({ fun_name: "token", data: token }));
      socket.onmessage = (event) => {
        if (this.socket !== socket || session.token !== token) return;
        let message: HallMessage;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message.fun_name === "token") {
          if (message.type === "error") {
            reject(new Error(message.message));
            this.close();
            clearSession(token);
            return;
          }
          setPlayer(message.data as Player);
          session.ready = true;
          clearInterval(this.heartbeat);
          this.heartbeat = setInterval(() => this.send("ping", {}), 1000);
          resolve();
        }
        if (
          ["getUserInfo", "PlayerInfo"].includes(message.fun_name) &&
          message.type === "success"
        )
          setPlayer(message.data as Player);
        const pending = this.pending.get(message.fun_name);
        if (pending) {
          this.pending.delete(message.fun_name);
          message.type === "success"
            ? pending.resolve(message.data)
            : pending.reject(
                new HallResponseError(message.message || "操作失败"),
              );
        }
        this.listeners.forEach((listener) => listener(message));
        if (
          message.fun_name === "KickOut" ||
          message.message === "身份已失效,请重新登录"
        ) {
          this.close();
          clearSession(token);
        }
      };
      socket.onerror = () => {
        /* onclose owns reconnection and pending failures. */
      };
      socket.onclose = (event) => {
        if (this.socket !== socket || session.token !== token) return;
        this.socket = null;
        clearInterval(this.heartbeat);
        session.ready = false;
        reject(new Error("大厅连接已断开"));
        for (const item of this.pending.values()) {
          item.reject(new Error("无法访问互联网,请检查网络连接"));
        }
        this.pending.clear();
        if (!this.stopped)
          this.retry = setTimeout(() => this.connect().catch(() => {}), 5000);
      };
    }).finally(() => {
      this.connecting = null;
    });
    return this.connecting;
  }
  private send(fun_name: string, data: unknown) {
    if (this.socket?.readyState === WebSocket.OPEN)
      this.socket.send(JSON.stringify({ fun_name, data }));
  }
  async command<T>(command: string, data: unknown): Promise<T> {
    await this.connect();
    if (this.pending.has(command)) throw new Error("操作处理中，请稍候");
    return new Promise<T>((resolve, reject) => {
      this.pending.set(command, {
        resolve: (value) => resolve(value as T),
        reject,
      });
      this.send(command, data);
    });
  }
  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  close() {
    this.stopped = true;
    this.machineId = 0;
    clearTimeout(this.retry);
    clearInterval(this.heartbeat);
    this.abortConnection?.();
    this.abortConnection = undefined;
    for (const item of this.pending.values()) {
      item.reject(new Error("大厅连接已断开"));
    }
    this.pending.clear();
    this.socket?.close(1000);
    this.socket = null;
    session.ready = false;
  }
}
export const hall = new HallConnection();
